import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Bed, Bath, Maximize, Search } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Properties = () => {
  const [searchParams] = useSearchParams();
  const locationParam = searchParams.get('location');
  
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    location: locationParam || '',
    property_type: '',
    status: 'available'
  });

  useEffect(() => {
    fetchProperties();
  }, [filters]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.location) params.append('location', filters.location);
      if (filters.property_type) params.append('property_type', filters.property_type);
      if (filters.status) params.append('status', filters.status);
      
      const response = await axios.get(`${API}/properties?${params.toString()}`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const locations = ['Mumbai', 'Lonavala', 'Alibaug', 'Dubai'];
  const propertyTypes = ['Villa', 'Penthouse', 'Apartment', 'Bungalow', 'Mansion'];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[50vh] w-full overflow-hidden" data-testid="properties-hero">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2, ease: 'easeOut' }}
          className="absolute inset-0"
        >
          <img
            src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&h=800&fit=crop&q=75"
            alt="Luxury Properties"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-primary/60" />
        </motion.div>

        <div className="relative h-full flex items-center justify-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center"
          >
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground tracking-tight mb-4">
              {filters.location ? `Properties in ${filters.location}` : 'Luxury Properties'}
            </h1>
            <p className="text-primary-foreground/90 text-lg max-w-2xl">
              Discover exceptional living spaces curated for discerning individuals
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-12 px-6 md:px-12 lg:px-24 bg-accent">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Location Filter */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Location</label>
              <select
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                data-testid="filter-location"
                className="w-full bg-white border border-muted px-4 py-3 focus:border-secondary focus:outline-none"
              >
                <option value="">All Locations</option>
                {locations.map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>

            {/* Property Type Filter */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Property Type</label>
              <select
                value={filters.property_type}
                onChange={(e) => setFilters({ ...filters, property_type: e.target.value })}
                data-testid="filter-type"
                className="w-full bg-white border border-muted px-4 py-3 focus:border-secondary focus:outline-none"
              >
                <option value="">All Types</option>
                {propertyTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                data-testid="filter-status"
                className="w-full bg-white border border-muted px-4 py-3 focus:border-secondary focus:outline-none"
              >
                <option value="">All Status</option>
                <option value="available">Available</option>
                <option value="sold">Sold</option>
                <option value="reserved">Reserved</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Properties Grid */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-background" data-testid="properties-grid">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">Loading properties...</p>
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-20">
              <Search className="mx-auto text-muted-foreground mb-4" size={48} />
              <h3 className="font-playfair text-2xl font-semibold text-primary mb-2">No Properties Found</h3>
              <p className="text-muted-foreground">Try adjusting your filters or check back later for new listings.</p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <p className="text-muted-foreground">
                  Found <span className="font-semibold text-primary">{properties.length}</span> {properties.length === 1 ? 'property' : 'properties'}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {properties.map((property, index) => (
                  <motion.div
                    key={property.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    data-testid={`property-${index}`}
                    className="group bg-white border border-muted hover:border-secondary transition-all duration-500 overflow-hidden hover:shadow-[0_20px_40px_-4px_rgba(15,40,30,0.2)]"
                  >
                    {/* Property Image */}
                    <div className="relative overflow-hidden h-64">
                      <img
                        src={property.images[0] || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800'}
                        alt={property.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      {property.featured && (
                        <div className="absolute top-4 left-4">
                          <span className="bg-secondary text-secondary-foreground px-3 py-1 text-xs font-bold uppercase">
                            Featured
                          </span>
                        </div>
                      )}
                      {property.status !== 'available' && (
                        <div className="absolute top-4 right-4">
                          <span className="bg-primary text-primary-foreground px-3 py-1 text-xs font-bold uppercase">
                            {property.status}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Property Details */}
                    <div className="p-6">
                      {/* Location & Type */}
                      <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                        <MapPin size={14} />
                        <span>{property.location} • {property.property_type}</span>
                      </div>

                      {/* Title */}
                      <h3 className="font-playfair text-xl font-semibold text-primary mb-3 group-hover:text-secondary transition-colors duration-300">
                        {property.title}
                      </h3>

                      {/* Description */}
                      <p className="text-muted-foreground text-sm leading-relaxed mb-4 line-clamp-2">
                        {property.description}
                      </p>

                      {/* Specifications */}
                      <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Bed size={16} />
                          <span>{property.bedrooms} Bed</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Bath size={16} />
                          <span>{property.bathrooms} Bath</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Maximize size={16} />
                          <span>{property.area_sqft.toLocaleString()} sq ft</span>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="border-t border-muted pt-4">
                        <div className="font-playfair text-2xl font-bold text-secondary">
                          {property.price}
                        </div>
                      </div>

                      {/* CTA */}
                      <a
                        href="/contact"
                        data-testid={`schedule-viewing-${index}`}
                        className="mt-4 block w-full bg-primary text-primary-foreground text-center py-3 font-manrope tracking-wide uppercase text-xs hover:bg-[#1A3F30] transition-all duration-300"
                      >
                        Schedule Viewing
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Properties;
